import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CompactActionBar from "../components/CompactActionBar";
import { useAuth } from "../contexts/AuthContext";
import { useHiddenMode } from "../contexts/HiddenModeContext";
import "../css/bookingSummary.css";
import "../css/standard-components.css";
import { usePageTitle } from "../hooks/usePageTitle";
import hiddenIncomeService from "../services/hiddenIncomeService";
import { getToken } from "../utils/authStorage";

interface Project {
  id: number;
  name: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  _count: {
    units: number;
  };
}

interface BookingSummaryData {
  id: number;
  bookingId: string;
  unitRef: string;
  paymentTerms?: string;
  customerName: string;
  contactNumber: string;
  saleDeedAmount: number;
  givenSDAmount: number;
  pendingSD: number;
  otherWorkIncome: number; // Changed from otherAllExpected to match API response
  hiddenIncomeExpected: number;
  hiddenIncomeReceived: number;
  hiddenIncomePending: number;
  bookingDate: string;
  status: string;
}

const BookingSummary: React.FC = () => {
  usePageTitle();
  const { user } = useAuth();
  const { isHiddenModeActive } = useHiddenMode();
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<string>("");
  const [bookings, setBookings] = useState<BookingSummaryData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Filter state
  const [customerNameFilter, setCustomerNameFilter] = useState("");
  const [mobileFilter, setMobileFilter] = useState("");
  const [unitFilter, setUnitFilter] = useState("");

  // Modal state for transaction history
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [transactionHistory, setTransactionHistory] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'income' | 'receivable'>('income');
  const [shouldLoadData, setShouldLoadData] = useState(false);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        setShowTransactionModal(false);
      }
    };

    if (showTransactionModal) {
      // Add listener first to capture before other handlers
      document.addEventListener('keydown', handleEscKey, { capture: true });
      return () => {
        document.removeEventListener('keydown', handleEscKey, { capture: true });
      };
    }
  }, [showTransactionModal]);

  // Redirect if not in hidden mode
  useEffect(() => {
    if (!isHiddenModeActive) {
      window.location.href = "/dashboard";
      return;
    }
  }, [isHiddenModeActive]);

  // Fetch projects on component mount
  useEffect(() => {
    if (isHiddenModeActive) {
      fetchProjects();
    }
  }, [isHiddenModeActive]);

  // Auto-select first project when projects are loaded
  useEffect(() => {
    if (projects.length > 0 && !selectedProject) {
      setSelectedProject(projects[0].id.toString());
    }
  }, [projects]);

  // Fetch bookings when load button is clicked or page changes
  useEffect(() => {
    if (selectedProject && isHiddenModeActive && shouldLoadData) {
      fetchBookingSummary();
    }
  }, [shouldLoadData, page, isHiddenModeActive]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const API_BASE_URL =
        process.env.REACT_APP_API_URL || "http://localhost:5010/api";
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/projects`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch projects");
      }

      const data = await response.json();
      setProjects(data || []);
    } catch (error) {
      console.error("Error fetching projects:", error);
      setError("Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  const fetchBookingSummary = async () => {
    try {
      setLoading(true);
      setError(null);
      const API_BASE_URL =
        process.env.REACT_APP_API_URL || "http://localhost:5010/api";
      const token = getToken();

      // ✅ Build query parameters with filters
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      if (customerNameFilter.trim()) {
        params.append("customerName", customerNameFilter.trim());
      }
      if (mobileFilter.trim()) {
        params.append("mobile", mobileFilter.trim());
      }
      if (unitFilter.trim()) {
        params.append("unit", unitFilter.trim());
      }

      const response = await fetch(
        `${API_BASE_URL}/booking-summary/project/${selectedProject}?${params}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "x-hidden-mode": "true", // Important for accessing hidden income data
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch booking summary");
      }

      const data = await response.json();
      setBookings(
        Array.isArray(data)
          ? data
          : data.success && Array.isArray(data.data)
          ? data.data
          : []
      );

      // ✅ Set pagination metadata
      if (data.pagination) {
        setTotalPages(data.pagination.pages);
        setTotalCount(data.pagination.total);
      }
    } catch (error) {
      console.error("Error fetching booking summary:", error);
      setError("Failed to load booking summary");
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  // ✅ NEW: Apply filters handler
  const handleApplyFilters = () => {
    setPage(1); // Reset to first page when filters change
    fetchBookingSummary();
  };

  // ✅ NEW: Clear filters handler
  const handleClearFilters = () => {
    setCustomerNameFilter("");
    setMobileFilter("");
    setUnitFilter("");
    setPage(1);
    // Fetch data immediately after clearing
    setTimeout(() => fetchBookingSummary(), 0);
  };

  // Handler for Load Data button
  const handleLoadData = () => {
    setPage(1);
    setShouldLoadData(true);
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatCurrencyWithDecimals = (amount: number): string => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getIncomeTypeLabel = (type: string): string => {
    switch (type) {
      case "charges":
      case "charges_work":
        return "Charges";
      case "terrace":
      case "terrace_work":
        return "Terrace Work";
      case "other_work":
        return "Other Work";
      case "banakhat":
      case "banakhat_work":
        return "Banakhat";
      case "adani_gas":
      case "adani_gas_work":
        return "Adani Gas";
      default:
        return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
  };

  const handleViewDetails = async (bookingId: number) => {
    try {
      setLoading(true);
      const history = await hiddenIncomeService.getBookingTransactionHistory(
        bookingId
      );
      setTransactionHistory(history);
      setShowTransactionModal(true);
    } catch (error) {
      console.error("Error loading transaction history:", error);
      setError("Failed to load transaction history");
    } finally {
      setLoading(false);
    }
  };

  // Show unauthorized if not in hidden mode
  if (!isHiddenModeActive) {
    return (
      <div className="unauthorized-access">
        <div className="unauthorized-content">
          <h2>🔒 Access Restricted</h2>
          <p>This page is only accessible in hidden mode.</p>
          <p>Please activate hidden mode to view booking summary.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container" style={{ background: '#fff' }}>
      {/* Compact Action Bar */}
      <CompactActionBar
        leftActions={
          <>
            <select
              value={selectedProject}
              onChange={(e) => {
                setSelectedProject(e.target.value);
                setShouldLoadData(false);
                // Clear filters when project changes
                setCustomerNameFilter("");
                setMobileFilter("");
                setUnitFilter("");
              }}
              style={{ 
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                minWidth: '200px'
              }}
              disabled={loading}
            >
              <option value="">-- Select Project --</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name} ({project._count.units} units)
                </option>
              ))}
            </select>
            {selectedProject && (
              <button
                onClick={handleLoadData}
                disabled={loading}
                style={{
                  padding: '8px 16px',
                  background: loading ? '#9ca3af' : '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontWeight: '500'
                }}
              >
                Load Data
              </button>
            )}
          </>
        }
        rightActions={<></>}
      />

      {/* Always Visible Filters */}
      {selectedProject && (
        <div style={{ padding: '16px 20px', background: '#f9fafb', borderTop: '1px solid #e5e7eb' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500' }}>
                Unit
              </label>
              <input
                type="text"
                value={unitFilter}
                onChange={(e) => setUnitFilter(e.target.value)}
                placeholder="G-301 (wing+unit) or 301 (all wings)"
                onKeyPress={(e) => e.key === "Enter" && handleApplyFilters()}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500' }}>
                Customer Name
              </label>
              <input
                type="text"
                value={customerNameFilter}
                onChange={(e) => setCustomerNameFilter(e.target.value)}
                placeholder="Search by customer name..."
                onKeyPress={(e) => e.key === "Enter" && handleApplyFilters()}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500' }}>
                Mobile Number
              </label>
              <input
                type="text"
                value={mobileFilter}
                onChange={(e) => setMobileFilter(e.target.value)}
                placeholder="Search by mobile..."
                onKeyPress={(e) => e.key === "Enter" && handleApplyFilters()}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
              <button 
                onClick={handleApplyFilters}
                style={{
                  padding: '8px 16px',
                  background: '#4f46e5',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Apply Filters
              </button>
              <button 
                onClick={handleClearFilters}
                style={{
                  padding: '8px 16px',
                  background: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div style={{ background: '#fff', margin: 0, padding: 0 }}>
        {/* Loading State */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
            Loading booking summary...
          </div>
        )}

        {/* Error State */}
        {error && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#dc2626' }}>
            <p>❌ {error}</p>
            <button
              onClick={() =>
                selectedProject ? fetchBookingSummary() : fetchProjects()
              }
              style={{
                padding: '8px 16px',
                background: '#4f46e5',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              Retry
            </button>
          </div>
        )}

        {/* Booking Summary Table */}
        {!loading && !error && selectedProject && bookings.length > 0 && (
          <>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '14px', color: '#6b7280' }}>
                  Showing {bookings.length} of {totalCount} bookings
                </span>
                <span style={{ fontSize: '14px', color: '#6b7280' }}>
                  Page {page} of {totalPages}
                </span>
              </div>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table className="standard-table">
                <thead>
                  <tr>
                    <th style={{ width: '60px', textAlign: 'center' }}>Sr No</th>
                    <th style={{ width: '100px' }}>Unit</th>
                    <th style={{ width: '130px' }}>Payment Terms</th>
                    <th style={{ minWidth: '150px' }}>Customer Name</th>
                    <th style={{ width: '120px' }}>Contact No</th>
                    <th style={{ width: '130px', textAlign: 'right' }}>Sale Deed Amount</th>
                    <th style={{ width: '130px', textAlign: 'right' }}>Given SD Amount</th>
                    <th style={{ width: '130px', textAlign: 'right' }}>Pending SD</th>
                    <th style={{ width: '140px', textAlign: 'right' }}>Other All (Expected)</th>
                    <th style={{ width: '140px', textAlign: 'right' }}>Given Hidden Amount</th>
                    <th style={{ width: '140px', textAlign: 'right' }}>Pending Hidden</th>
                    <th style={{ width: '110px' }}>Booking Date</th>
                    <th style={{ width: '100px' }}>Status</th>
                    <th style={{ width: '80px', textAlign: 'center' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((booking, index) => {
                    // Calculate reverse Sr No: total - (page-1)*limit - index
                    const reverseSrNo = totalCount - (page - 1) * limit - index;
                    return (
                      <tr key={booking.id} style={{ cursor: 'pointer' }} onClick={() => handleViewDetails(booking.id)}>
                        <td style={{ textAlign: 'center', fontWeight: '500' }}>{reverseSrNo}</td>
                        <td style={{ fontWeight: '600', color: '#4f46e5' }}>{booking.unitRef}</td>
                        <td style={{ fontSize: '13px', color: '#374151' }}>{booking.paymentTerms || '-'}</td>
                        <td>{booking.customerName}</td>
                        <td style={{ fontFamily: 'monospace', fontSize: '13px' }}>{booking.contactNumber}</td>
                        <td style={{ textAlign: 'right', fontWeight: '600', color: booking.saleDeedAmount < 0 ? '#dc2626' : '#059669', background: '#f0f4ff' }}>
                          {formatCurrency(booking.saleDeedAmount)}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: '600', color: booking.givenSDAmount < 0 ? '#dc2626' : '#059669', background: '#f0fdf4' }}>
                          {formatCurrency(booking.givenSDAmount)}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: '600', color: booking.pendingSD < 0 ? '#dc2626' : '#059669', background: '#fef2f2' }}>
                          {formatCurrency(booking.pendingSD)}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: '600', color: booking.otherWorkIncome < 0 ? '#dc2626' : '#059669', background: '#f0f4ff' }}>
                          {formatCurrencyWithDecimals(booking.otherWorkIncome)}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: '600', color: booking.hiddenIncomeReceived < 0 ? '#dc2626' : '#059669', background: '#f0fdf4' }}>
                          {formatCurrencyWithDecimals(booking.hiddenIncomeReceived)}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: '600', color: booking.hiddenIncomePending < 0 ? '#dc2626' : '#059669', background: '#fef2f2' }}>
                          {formatCurrencyWithDecimals(booking.hiddenIncomePending)}
                        </td>
                        <td style={{ fontSize: '13px', color: '#6b7280' }}>
                          {formatDate(booking.bookingDate)}
                        </td>
                        <td>
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '500',
                            background: booking.status === 'active' ? '#d1fae5' : booking.status === 'completed' ? '#dbeafe' : '#fee2e2',
                            color: booking.status === 'active' ? '#065f46' : booking.status === 'completed' ? '#1e40af' : '#991b1b'
                          }}>
                            {booking.status}
                          </span>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewDetails(booking.id);
                            }}
                            style={{
                              padding: '6px 12px',
                              background: '#4f46e5',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '13px',
                              cursor: 'pointer',
                              fontWeight: '500'
                            }}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ 
                padding: '16px 20px',
                borderTop: '1px solid #e5e7eb',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '16px'
              }}>
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  style={{
                    padding: '8px 16px',
                    background: page === 1 ? '#e5e7eb' : '#4f46e5',
                    color: page === 1 ? '#9ca3af' : 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: page === 1 ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  Previous
                </button>
                <span style={{ fontSize: '14px', color: '#6b7280' }}>
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page >= totalPages}
                  style={{
                    padding: '8px 16px',
                    background: page >= totalPages ? '#e5e7eb' : '#4f46e5',
                    color: page >= totalPages ? '#9ca3af' : 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: page >= totalPages ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}

        {/* No bookings message */}
        {!loading && !error && selectedProject && bookings.length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#6b7280' }}>
            <p style={{ fontSize: '16px' }}>📋 No bookings found for the selected project.</p>
          </div>
        )}
      </div>

      {/* Transaction History Modal - Full Screen */}
      {showTransactionModal && transactionHistory && (
        <div className="modal-overlay fullscreen-modal-overlay">
          <div className="modal-content fullscreen-modal-content">
            <div className="modal-header fullscreen-modal-header">
              <h2>Complete Transaction History</h2>
              <button
                onClick={() => setShowTransactionModal(false)}
                className="close-button"
              >
                ×
              </button>
            </div>

            <div className="modal-body fullscreen-modal-body">
              {/* Three Column Layout - Booking Info | Customers | Financial Summary */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(3, 1fr)', 
                gap: '20px', 
                marginBottom: '30px',
                background: '#f9fafb',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                {/* Column 1: Booking Information */}
                <div style={{ 
                  background: 'white', 
                  padding: '16px', 
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb'
                }}>
                  <h4 style={{ 
                    margin: '0 0 16px 0', 
                    fontSize: '15px', 
                    fontWeight: '600',
                    color: '#1f2937',
                    borderBottom: '2px solid #4f46e5',
                    paddingBottom: '8px'
                  }}>📋 Booking Information</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div>
                      <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500' }}>Unit Number</div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#059669' }}>
                        {transactionHistory.booking.unitRef}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500' }}>Unit Type</div>
                      <div style={{ fontSize: '13px', color: '#374151' }}>
                        {transactionHistory.booking.unitType || 'N/A'}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500' }}>Unit Size</div>
                      <div style={{ fontSize: '13px', color: '#374151' }}>
                        {transactionHistory.booking.unitSize ? `${transactionHistory.booking.unitSize} sq.ft.` : 'N/A'}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500' }}>Booking Date</div>
                      <div style={{ fontSize: '13px', color: '#374151' }}>
                        {formatDate(transactionHistory.booking.bookingDate)}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500' }}>Payment Terms</div>
                      <div style={{ fontSize: '13px', color: '#374151' }}>
                        {transactionHistory.booking.paymentTerms || 'N/A'}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500' }}>Remarks</div>
                      <div style={{ fontSize: '12px', color: '#6b7280', fontStyle: transactionHistory.booking.remarks ? 'normal' : 'italic' }}>
                        {transactionHistory.booking.remarks || 'No remarks'}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500' }}>Status</div>
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '500',
                        background: transactionHistory.booking.status === 'active' ? '#d1fae5' : 
                                   transactionHistory.booking.status === 'completed' ? '#dbeafe' : '#fee2e2',
                        color: transactionHistory.booking.status === 'active' ? '#065f46' : 
                               transactionHistory.booking.status === 'completed' ? '#1e40af' : '#991b1b'
                      }}>
                        {transactionHistory.booking.status}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Column 2: Customer Information */}
                <div style={{ 
                  background: 'white', 
                  padding: '16px', 
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb'
                }}>
                  <h4 style={{ 
                    margin: '0 0 16px 0', 
                    fontSize: '15px', 
                    fontWeight: '600',
                    color: '#1f2937',
                    borderBottom: '2px solid #4f46e5',
                    paddingBottom: '8px'
                  }}>👥 Customers</h4>
                  {transactionHistory.booking.applicants && transactionHistory.booking.applicants.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {transactionHistory.booking.applicants.map((applicant: any) => (
                        <div key={`applicant-${applicant.id}`} style={{ 
                          padding: '10px', 
                          borderRadius: '6px',
                          background: applicant.isPrimary ? '#eef2ff' : '#f9fafb',
                          border: applicant.isPrimary ? '1px solid #c7d2fe' : '1px solid #e5e7eb'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                            <div style={{ fontSize: '13px', fontWeight: '600', color: applicant.isPrimary ? '#4f46e5' : '#374151' }}>
                              {applicant.firstName} {applicant.surname}
                            </div>
                            {applicant.isPrimary && (
                              <span style={{ 
                                fontSize: '10px', fontWeight: '600', color: '#fff', 
                                background: '#4f46e5', padding: '2px 6px', borderRadius: '4px' 
                              }}>PRIMARY</span>
                            )}
                          </div>
                          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '3px' }}>
                            📞 {applicant.contactNumber || 'No contact'}
                          </div>
                          {applicant.panCard && (
                            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '3px' }}>
                              PAN: <span style={{ fontFamily: 'monospace', fontWeight: '500', color: '#374151' }}>{applicant.panCard}</span>
                            </div>
                          )}
                          {applicant.aadharCard && (
                            <div style={{ fontSize: '12px', color: '#6b7280' }}>
                              Aadhar: <span style={{ fontFamily: 'monospace', fontWeight: '500', color: '#374151' }}>{applicant.aadharCard}</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ fontSize: '13px', color: '#6b7280' }}>
                      {transactionHistory.booking.customerName || 'No customer information'}
                    </div>
                  )}
                </div>

                {/* Column 3: Financial Summary */}
                <div style={{ 
                  background: 'white', 
                  padding: '16px', 
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb'
                }}>
                  <h4 style={{ 
                    margin: '0 0 16px 0', 
                    fontSize: '15px', 
                    fontWeight: '600',
                    color: '#1f2937',
                    borderBottom: '2px solid #4f46e5',
                    paddingBottom: '8px'
                  }}>💰 Financial Summary</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {/* Sale Deed Section */}
                    <div style={{ background: '#f0f4ff', padding: '8px', borderRadius: '4px' }}>
                      <div style={{ fontSize: '11px', color: '#4f46e5', fontWeight: '600', marginBottom: '6px' }}>
                        SALE DEED
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '3px' }}>
                        <span style={{ color: '#6b7280' }}>Expected:</span>
                        <span style={{ fontWeight: '600', color: '#374151' }}>
                          {formatCurrency(transactionHistory.mainIncome.expectedAmount)}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '3px' }}>
                        <span style={{ color: '#6b7280' }}>Received:</span>
                        <span style={{ fontWeight: '600', color: '#059669' }}>
                          {formatCurrency(transactionHistory.mainIncome.receivedAmount)}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '3px' }}>
                        <span style={{ color: '#6b7280' }}>Pending for Approval:</span>
                        <span style={{ fontWeight: '600', color: '#f59e0b' }}>
                          {formatCurrency(transactionHistory.mainIncome.pendingForApproval || 0)}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                        <span style={{ color: '#6b7280' }}>Pending:</span>
                        <span style={{ fontWeight: '600', color: '#dc2626' }}>
                          {formatCurrency(transactionHistory.mainIncome.pendingAmount)}
                        </span>
                      </div>
                    </div>

                    {/* Receivables Section */}
                    <div style={{ background: '#f0fdf4', padding: '8px', borderRadius: '4px' }}>
                      <div style={{ fontSize: '11px', color: '#059669', fontWeight: '600', marginBottom: '6px' }}>
                        RECEIVABLES
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '3px' }}>
                        <span style={{ color: '#6b7280' }}>Expected:</span>
                        <span style={{ fontWeight: '600', color: '#374151' }}>
                          {formatCurrencyWithDecimals(transactionHistory.hiddenIncome.totalExpectedAmount)}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '3px' }}>
                        <span style={{ color: '#6b7280' }}>Received:</span>
                        <span style={{ fontWeight: '600', color: '#059669' }}>
                          {formatCurrencyWithDecimals(transactionHistory.hiddenIncome.totalReceivedAmount)}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '3px' }}>
                        <span style={{ color: '#6b7280' }}>Pending for Approval:</span>
                        <span style={{ fontWeight: '600', color: '#f59e0b' }}>
                          {formatCurrencyWithDecimals(transactionHistory.hiddenIncome.totalPendingForApproval || 0)}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                        <span style={{ color: '#6b7280' }}>Pending:</span>
                        <span style={{ fontWeight: '600', color: '#dc2626' }}>
                          {formatCurrencyWithDecimals(transactionHistory.hiddenIncome.totalPendingAmount)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tabs for Income and Receivable */}
              <div className="transactions-table-section">
                <div style={{ borderBottom: '2px solid #e5e7eb', marginBottom: '20px' }}>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <button
                      onClick={() => setActiveTab('income')}
                      style={{
                        padding: '12px 24px',
                        border: 'none',
                        background: activeTab === 'income' ? '#4f46e5' : 'transparent',
                        color: activeTab === 'income' ? 'white' : '#6b7280',
                        borderBottom: activeTab === 'income' ? '3px solid #4f46e5' : '3px solid transparent',
                        cursor: 'pointer',
                        fontSize: '15px',
                        fontWeight: '600',
                        borderRadius: '8px 8px 0 0',
                        transition: 'all 0.2s'
                      }}
                    >
                      💰 Sale Deed Income ({transactionHistory.mainIncome.payments.length})
                    </button>
                    <button
                      onClick={() => setActiveTab('receivable')}
                      style={{
                        padding: '12px 24px',
                        border: 'none',
                        background: activeTab === 'receivable' ? '#4f46e5' : 'transparent',
                        color: activeTab === 'receivable' ? 'white' : '#6b7280',
                        borderBottom: activeTab === 'receivable' ? '3px solid #4f46e5' : '3px solid transparent',
                        cursor: 'pointer',
                        fontSize: '15px',
                        fontWeight: '600',
                        borderRadius: '8px 8px 0 0',
                        transition: 'all 0.2s'
                      }}
                    >
                      📋 Receivable Income ({transactionHistory.hiddenIncome.transactionsByType?.reduce((sum: number, type: any) => sum + type.installments.length, 0) || 0})
                    </button>
                  </div>
                </div>

                {/* Income Tab Content - Match Income Page Structure */}
                {activeTab === 'income' && (
                  <>
                    <div className="transactions-table-container">
                      <table className="transactions-table" style={{ width: '100%' }}>
                        <thead>
                          <tr>
                            <th style={{ width: '60px', textAlign: 'center' }}>Sr. No</th>
                            <th style={{ minWidth: '140px' }}>Customer</th>
                            <th style={{ minWidth: '100px' }}>Project</th>
                            <th style={{ width: '90px' }}>Unit</th>
                            <th style={{ width: '120px', textAlign: 'right' }}>Amount</th>
                            <th style={{ minWidth: '90px' }}>Receipt No</th>
                            <th style={{ width: '110px' }}>Received Date</th>
                            <th style={{ width: '120px' }}>Status</th>
                            <th style={{ width: '110px' }}>Payment Method</th>
                            <th style={{ minWidth: '110px' }}>Bank Name</th>
                            <th style={{ minWidth: '100px' }}>Reference</th>
                            <th style={{ minWidth: '100px' }}>Remarks</th>
                            <th style={{ width: '110px' }}>Instrument Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {transactionHistory.mainIncome.payments.length > 0 ? (
                            transactionHistory.mainIncome.payments.map((payment: any, index: number) => (
                              <tr key={`income-${payment.id}`}>
                                <td style={{ textAlign: 'center', fontWeight: '600', color: '#4f46e5' }}>
                                  {index + 1}
                                </td>
                                <td>
                                  <div style={{ fontSize: '13px', maxWidth: '160px', whiteSpace: 'normal', lineHeight: '1.3' }}>
                                    {payment.receiptName || transactionHistory.booking.customerName || 'N/A'}
                                  </div>
                                </td>
                                <td>
                                  <span style={{ fontSize: '13px', fontWeight: '500' }}>
                                    {transactionHistory.booking.projectName || 'N/A'}
                                  </span>
                                </td>
                                <td>
                                  <span style={{ fontSize: '13px', fontWeight: '500' }}>
                                    {transactionHistory.booking.unitRef || 'N/A'}
                                  </span>
                                </td>
                                <td style={{ textAlign: 'right', fontWeight: '600', color: payment.amount < 0 ? '#dc2626' : '#059669' }}>
                                  {formatCurrency(payment.amount)}
                                </td>
                                <td style={{ fontSize: '12px' }}>
                                  {payment.receiptNo || '-'}
                                </td>
                                <td style={{ fontSize: '13px' }}>
                                  {payment.chequeReceivedDate
                                    ? formatDate(payment.chequeReceivedDate)
                                    : payment.paymentDate
                                    ? formatDate(payment.paymentDate)
                                    : '-'}
                                </td>
                                <td>
                                  {payment.status === 'approved' ? (
                                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#dcfce7', color: '#166534', padding: '3px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 500 }}>
                                      <span style={{ width: '8px', height: '8px', borderRadius: '2px', background: '#16a34a', display: 'inline-block' }} />
                                      {payment.approvedAt ? formatDate(payment.approvedAt) : 'Approved'}
                                    </div>
                                  ) : (
                                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#fef2f2', color: '#991b1b', padding: '3px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 500 }}>
                                      <span style={{ width: '8px', height: '8px', borderRadius: '2px', background: '#dc2626', display: 'inline-block' }} />
                                      Pending
                                    </div>
                                  )}
                                </td>
                                <td style={{ fontSize: '13px' }}>
                                  {payment.paymentMode || '-'}
                                </td>
                                <td style={{ fontSize: '13px' }}>
                                  {payment.bankName || '-'}
                                </td>
                                <td style={{ fontSize: '12px' }}>
                                  {payment.referenceNumber || '-'}
                                </td>
                                <td style={{ fontSize: '12px', color: '#6b7280' }}>
                                  {payment.incomeRemarks || payment.remarks || '-'}
                                </td>
                                <td style={{ fontSize: '13px' }}>
                                  {payment.paymentMode?.toLowerCase() === 'cheque' && payment.instrumentDate
                                    ? formatDate(payment.instrumentDate)
                                    : payment.paymentDate
                                    ? formatDate(payment.paymentDate)
                                    : '-'}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={13} style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                                No sale deed income transactions found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}

                {/* Receivable Income Tab Content - Match Hidden Income Page Structure */}
                {activeTab === 'receivable' && (
                  <>
                    <div className="transactions-table-container">
                      <table className="transactions-table" style={{ width: '100%' }}>
                        <thead>
                          <tr>
                            <th style={{ width: '60px', textAlign: 'center' }}>Sr. No</th>
                            <th style={{ minWidth: '140px' }}>Customer</th>
                            <th style={{ width: '100px' }}>Unit</th>
                            <th style={{ minWidth: '100px' }}>Project</th>
                            <th style={{ width: '140px' }}>Income Type</th>
                            <th style={{ width: '130px', textAlign: 'right' }}>Amount</th>
                            <th style={{ width: '110px' }}>Income Date</th>
                            <th style={{ width: '120px' }}>Authorized</th>
                            <th style={{ width: '100px', textAlign: 'center' }}>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {transactionHistory.hiddenIncome.transactionsByType && 
                           transactionHistory.hiddenIncome.transactionsByType.length > 0 ? (
                            transactionHistory.hiddenIncome.transactionsByType.flatMap((incomeType: any) =>
                              incomeType.installments.map((installment: any, instIndex: number) => {
                                const globalIndex = transactionHistory.hiddenIncome.transactionsByType
                                  .slice(0, transactionHistory.hiddenIncome.transactionsByType.indexOf(incomeType))
                                  .reduce((sum: number, type: any) => sum + type.installments.length, 0) + instIndex + 1;
                                
                                return (
                                  <tr key={`receivable-${installment.id}`}>
                                    <td style={{ textAlign: 'center', fontWeight: '600', color: '#4f46e5' }}>
                                      {globalIndex}
                                    </td>
                                    <td>
                                      <div style={{ fontSize: '13px', maxWidth: '160px', whiteSpace: 'normal', lineHeight: '1.3' }}>
                                        {installment.customerName || transactionHistory.booking.customerName || 'N/A'}
                                      </div>
                                    </td>
                                    <td>
                                      <span style={{ fontSize: '13px', fontWeight: '500' }}>
                                        {transactionHistory.booking.unitRef || 'N/A'}
                                      </span>
                                    </td>
                                    <td>
                                      <span style={{ fontSize: '13px', fontWeight: '500' }}>
                                        {transactionHistory.booking.projectName || 'N/A'}
                                      </span>
                                    </td>
                                    <td>
                                      <span style={{
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        fontSize: '12px',
                                        fontWeight: '500',
                                        background: incomeType.incomeType === 'charges' ? '#fef3c7' :
                                                   incomeType.incomeType === 'terrace' ? '#dbeafe' :
                                                   incomeType.incomeType === 'banakhat' ? '#fce7f3' : '#f3f4f6',
                                        color: incomeType.incomeType === 'charges' ? '#92400e' :
                                               incomeType.incomeType === 'terrace' ? '#1e40af' :
                                               incomeType.incomeType === 'banakhat' ? '#9f1239' : '#374151'
                                      }}>
                                        {getIncomeTypeLabel(incomeType.incomeType)}
                                      </span>
                                    </td>
                                    <td style={{ textAlign: 'right', fontWeight: '500', color: '#059669', fontSize: '14px' }}>
                                      {formatCurrency(installment.amount)}
                                    </td>
                                    <td style={{ fontSize: '13px' }}>
                                      {formatDate(installment.incomeDate)}
                                    </td>
                                    <td>
                                      {installment.isApproved && installment.approvedAt && !String(installment.approvedAt).startsWith('[') ? (
                                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#dcfce7', color: '#166534', padding: '3px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 500 }}>
                                          <span style={{ width: '8px', height: '8px', borderRadius: '2px', background: '#16a34a', display: 'inline-block' }} />
                                          {formatDate(installment.approvedAt)}
                                        </div>
                                      ) : (
                                        <span style={{ color: '#6b7280', fontSize: '12px' }}>-</span>
                                      )}
                                    </td>
                                    <td style={{ textAlign: 'center' }}>
                                      {installment.isApproved ? (
                                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#dcfce7', color: '#166534', padding: '3px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 500 }}>
                                          <span style={{ width: '8px', height: '8px', borderRadius: '2px', background: '#16a34a', display: 'inline-block' }} />
                                          Approved
                                        </div>
                                      ) : (
                                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#fef2f2', color: '#991b1b', padding: '3px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 500 }}>
                                          <span style={{ width: '8px', height: '8px', borderRadius: '2px', background: '#dc2626', display: 'inline-block' }} />
                                          Pending
                                        </div>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })
                            )
                          ) : (
                            <tr>
                              <td colSpan={9} style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                                No receivable income transactions found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button
                onClick={() => setShowTransactionModal(false)}
                className="cancel-button"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingSummary;
